import { useId, CSSProperties } from 'react'

interface MagneticStirrerProps {
  speed?: number // 0-10
  isOn?: boolean
  hasFlask?: boolean
  onClick?: () => void
  disabled?: boolean
  className?: string
}

const MagneticStirrer = ({
  speed = 0,
  isOn = false,
  hasFlask = false,
  onClick,
  disabled = false,
  className = ''
}: MagneticStirrerProps) => {
  const id = useId()

  const containerStyle: CSSProperties = {
    cursor: disabled ? 'default' : onClick ? 'pointer' : 'default',
    opacity: disabled ? 0.5 : 1,
    transition: 'all 0.2s ease'
  }

  // Calculate dial rotation based on speed
  const dialRotation = (speed / 10) * 270 - 135 // -135 to +135 degrees

  // LED color based on state
  const ledColor = isOn ? '#00FF00' : '#333333'
  const ledGlow = isOn ? '0 0 8px #00FF00' : 'none'

  return (
    <svg
      viewBox="0 0 140 80"
      width={140}
      height={80}
      style={containerStyle}
      className={className}
      onClick={disabled ? undefined : onClick}
    >
      <defs>
        {/* Main body gradient */}
        <linearGradient id={`body-${id}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#E8E8E8" />
          <stop offset="20%" stopColor="#D0D0D0" />
          <stop offset="80%" stopColor="#B8B8B8" />
          <stop offset="100%" stopColor="#A0A0A0" />
        </linearGradient>

        {/* Top plate gradient */}
        <linearGradient id={`plate-${id}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#F5F5F5" />
          <stop offset="50%" stopColor="#E8E8E8" />
          <stop offset="100%" stopColor="#D0D0D0" />
        </linearGradient>

        {/* Ceramic top gradient */}
        <linearGradient id={`ceramic-${id}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#FFFFFF" />
          <stop offset="30%" stopColor="#F8F8F8" />
          <stop offset="100%" stopColor="#E8E8E8" />
        </linearGradient>

        {/* Dial gradient */}
        <radialGradient id={`dial-${id}`} cx="50%" cy="30%" r="70%">
          <stop offset="0%" stopColor="#4A4A4A" />
          <stop offset="80%" stopColor="#2A2A2A" />
          <stop offset="100%" stopColor="#1A1A1A" />
        </radialGradient>

        {/* Shadow */}
        <filter id={`shadow-${id}`} x="-10%" y="-10%" width="120%" height="130%">
          <feDropShadow dx="2" dy="3" stdDeviation="3" floodOpacity="0.3"/>
        </filter>

        {/* Spinning magnetic field indicator */}
        {isOn && (
          <filter id={`field-glow-${id}`} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        )}
      </defs>

      <g filter={`url(#shadow-${id})`}>
        {/* Main housing */}
        <rect
          x="10"
          y="30"
          width="120"
          height="45"
          rx="5"
          fill={`url(#body-${id})`}
          stroke="#909090"
          strokeWidth="1"
        />

        {/* Front panel detail */}
        <rect
          x="10"
          y="55"
          width="120"
          height="20"
          fill="#C8C8C8"
          stroke="#909090"
          strokeWidth="0.5"
        />

        {/* Top plate */}
        <rect
          x="15"
          y="25"
          width="110"
          height="10"
          rx="2"
          fill={`url(#plate-${id})`}
          stroke="#A0A0A0"
          strokeWidth="1"
        />

        {/* Ceramic stirring surface */}
        <rect
          x="30"
          y="20"
          width="80"
          height="15"
          rx="3"
          fill={`url(#ceramic-${id})`}
          stroke="#D0D0D0"
          strokeWidth="1"
        />

        {/* Center indicator circle */}
        <circle
          cx="70"
          cy="27"
          r="8"
          fill="none"
          stroke="#E0E0E0"
          strokeWidth="1"
          strokeDasharray="2,2"
        />

        {/* Magnetic field indicator (spinning when on) */}
        {isOn && (
          <g filter={`url(#field-glow-${id})`}>
            <ellipse
              cx="70"
              cy="27"
              rx="6"
              ry="3"
              fill="none"
              stroke="rgba(100, 150, 255, 0.5)"
              strokeWidth="1"
            >
              <animateTransform
                attributeName="transform"
                type="rotate"
                from="0 70 27"
                to="360 70 27"
                dur={`${1.1 - speed/10}s`}
                repeatCount="indefinite"
              />
            </ellipse>
          </g>
        )}

        {/* Flask placement indicator ring */}
        {hasFlask && (
          <circle
            cx="70"
            cy="27"
            r="20"
            fill="none"
            stroke="#A0D0A0"
            strokeWidth="2"
            opacity="0.6"
          />
        )}

        {/* Speed control dial */}
        <g transform="translate(25, 62)">
          {/* Dial base */}
          <circle
            cx="0"
            cy="0"
            r="10"
            fill={`url(#dial-${id})`}
            stroke="#1A1A1A"
            strokeWidth="1"
          />

          {/* Dial indicator line */}
          <line
            x1="0"
            y1="-2"
            x2="0"
            y2="-8"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            transform={`rotate(${dialRotation})`}
          />

          {/* Dial grip marks */}
          {[0, 45, 90, 135, 180, 225, 270, 315].map((angle) => (
            <line
              key={angle}
              x1="0"
              y1="-9"
              x2="0"
              y2="-10"
              stroke="#666"
              strokeWidth="1"
              transform={`rotate(${angle})`}
            />
          ))}
        </g>

        {/* Speed label */}
        <text
          x="25"
          y="50"
          fontSize="6"
          fill="#666"
          textAnchor="middle"
          fontFamily="Arial, sans-serif"
        >
          SPEED
        </text>

        {/* Min/Max labels */}
        <text x="12" y="68" fontSize="5" fill="#666" fontFamily="Arial, sans-serif">0</text>
        <text x="35" y="68" fontSize="5" fill="#666" fontFamily="Arial, sans-serif">10</text>

        {/* Power LED */}
        <circle
          cx="115"
          cy="62"
          r="4"
          fill={ledColor}
          style={{ boxShadow: ledGlow }}
        />
        {isOn && (
          <circle
            cx="115"
            cy="62"
            r="6"
            fill="none"
            stroke="#00FF00"
            strokeWidth="1"
            opacity="0.5"
          >
            <animate
              attributeName="opacity"
              values="0.5;0.2;0.5"
              dur="1s"
              repeatCount="indefinite"
            />
          </circle>
        )}

        {/* Power label */}
        <text
          x="115"
          y="50"
          fontSize="6"
          fill="#666"
          textAnchor="middle"
          fontFamily="Arial, sans-serif"
        >
          ON
        </text>

        {/* Speed indicator display */}
        <rect
          x="55"
          y="55"
          width="30"
          height="16"
          rx="2"
          fill="#1A1A1A"
          stroke="#3A3A3A"
          strokeWidth="1"
        />
        <text
          x="70"
          y="67"
          fontSize="10"
          fill={isOn ? '#00FF00' : '#333'}
          textAnchor="middle"
          fontFamily="'Courier New', monospace"
          fontWeight="bold"
        >
          {isOn ? speed.toFixed(0) : '--'}
        </text>

        {/* Brand label */}
        <text
          x="70"
          y="77"
          fontSize="5"
          fill="#888"
          textAnchor="middle"
          fontFamily="Arial, sans-serif"
          letterSpacing="1"
        >
          MAGNETIC STIRRER
        </text>

        {/* Ventilation grilles */}
        {[0, 1, 2].map((i) => (
          <g key={i}>
            <line
              x1={95 + i * 6}
              y1="60"
              x2={95 + i * 6}
              y2="70"
              stroke="#909090"
              strokeWidth="2"
            />
          </g>
        ))}

        {/* Corner feet shadows */}
        <ellipse cx="18" cy="76" rx="6" ry="2" fill="rgba(0,0,0,0.2)" />
        <ellipse cx="122" cy="76" rx="6" ry="2" fill="rgba(0,0,0,0.2)" />
      </g>
    </svg>
  )
}

export default MagneticStirrer
